import { getPageFromPathname } from './getPageFromPathname';
import {
    BOOKINGCOMPLETE_GA_CATEGORY as BookingCompleteCategory,
    GaActionText as BookingCompleteActionText,
} from '../Booking/pages/BookingComplete/constants';
import {
    APPOINTMENT_INFO_GA_CATEGORY as AppointmentInfoCategory,
    GaActionText as AppointmentInfoActionText,
} from '../Booking/pages/AppointmentInfo/constants';
import {
    APPOINTMENT_DETAILS_GA_CATEGORY as AppointmentDetailsCategory,
    GaActionText as AppointmentDetailsActionText,
} from '../Booking/pages/AppointmentDetails/constants';

const trackEventWhitelist = initTrackEventWhitelist();

export const AnalyticsTypes = {
    TrackEvent: 0,
    ChangePageView: 1,
    SetDimension: 2,
    LinkDomains: 3,
};

export const SettingType = {
    CustomDimension: 0,
    ContentGroup: 1,
};

export const DimensionKeys = {
    AbTestIds: 1,
    Specialty: 2,
    Placemark: 3,
    Procedure: 4,
    InsuranceCarrier: 5,
    InsurancePlan: 6,
    Language: 7,
    Market: 8,
    Brand: 9,
    Professional: 10,
    Provider: 11,
    // 12 is unused
    UserLoginStatus: 13,
    UserType: 14,
    ZlogPageType: 15,
    PageTypeZocMon: 16,
    ProfessionalActiveStatus: 17,
    Product: 18,
    WhiteLabel: 22,
    ProviderID: 23,
    StrategicID: 24,
    PatientType: 25,
    BookingId: 26,
    UserExperienceTest: 27,
};

export const ContentGroupKeys = {
    ZlogPageType: 1,
    ZlogRollupBucket: 2,
};

export const DimensionValues = {};
Object.keys(DimensionKeys).forEach(key => {
    DimensionValues[DimensionKeys[key]] = key;
});

export const ContentValues = {};
Object.keys(ContentGroupKeys).forEach(key => {
    ContentValues[ContentGroupKeys[key]] = key;
});

export const InteractionLabels = {
    Click: 'click',
    Keyboard: 'keyboard',
    Swipe: 'swipe',
};

export const RedirectLabels = {
    Internal: 'internal-redirect',
    External: 'external-redirect',
    NoRedirect: null,
};

let domContentLoaded = false;

if (typeof document !== 'undefined' && document.readyState === 'loading') {
    // The GA tracking contentGroup information for booking flow gets set
    // as a callback on DOMContentLoaded, so we need to wait for that.
    document.addEventListener('DOMContentLoaded', () => {
        // This setTimeout(fn, 0) pushes the execution of the inner code to
        // the back of the javascript execution stack.  This way our code gets
        // executed after any other code on DOMContentLoaded.
        setTimeout(() => {
            domContentLoaded = true;
        }, 0);
    });
} else {
    domContentLoaded = true;
}

function prefixWithTags(action, category, tags) {
    const buildingPrefixes = [];

    if (category && category !== '') {
        buildingPrefixes.push(`[${category}]`);
    }

    if (tags && tags.length > 0) {
        buildingPrefixes.push(`[${tags}]`);
    }

    buildingPrefixes.push(action);

    return buildingPrefixes.join(' ');
}

async function getEventTrackingObject(data, defaultCategory) {
    const listedCategory = data.category || defaultCategory;
    const listedAction =
        prefixWithTags(data.action, data.category, data.tags) || '';

    return {
        hitType: 'event',
        eventCategory: listedCategory,
        eventAction: listedAction,
        eventLabel: data.label,
        eventValue: data.value,
    };
}

/**
 * Whitelist the following (category,action) pairs:
 * [Booking - Appt Info] Click Continue
 * [Booking - Details] Click Book Appointment
 * [Booking - Details] BookingApptClicked
 * [BookingComplete] Booked
 *
 * Use unique values to form the whitelist. A value is created by concatenating
 * the action and category strings, using a "!" as an arbitrary delimiter.
 */
function initTrackEventWhitelist() {
    return new Set([
        `${AppointmentInfoCategory}!${AppointmentInfoActionText.trackClickContinue}`,
        `${AppointmentDetailsCategory}!${AppointmentDetailsActionText.clickBookAppointment}`,
        `${AppointmentDetailsCategory}!${AppointmentDetailsActionText.bookingApptClicked}`,
        `${BookingCompleteCategory}!${BookingCompleteActionText.trackBooked}`,
        `Booking - Booking Complete!${BookingCompleteActionText.trackMadeItToBookingComplete}`,
    ]);
}

function trackEventIsWhitelisted(data, defaultCategory) {
    const key = `${data.category}!${data.action}`;
    const defaultKey = `${defaultCategory}!${data.action}`;
    return trackEventWhitelist.has(key) || trackEventWhitelist.has(defaultKey);
}

async function trackEventWithData(tracker, data, defaultCategory) {
    const trackingObject = await getEventTrackingObject(data, defaultCategory);
    tracker('send', trackingObject);
}

function determineSettingType(settingType) {
    switch (settingType) {
        case SettingType.ContentGroup:
            return 'contentGroup';
        case SettingType.CustomDimension:
            return 'dimension';
        default:
            throw new Error('Please specify a valid setting type');
    }
}

export function createAnalyticsLogger(ga) {
    const _gaEventQueue = [];
    let _gaCheckLoop;

    function checkingLoop() {
        const tracker =
            typeof window !== 'undefined' && window.ga ? window.ga : undefined;

        if (!tracker || !domContentLoaded) {
            return;
        }

        clearInterval(_gaCheckLoop);
        _gaCheckLoop = undefined;

        for (const action of _gaEventQueue) {
            processAction(action, tracker);
        }
    }

    function processAction(action, tracker) {
        let analyticsArray = [action.meta.analytics];
        if (Array.isArray(action.meta.analytics)) {
            analyticsArray = action.meta.analytics;
        }

        for (let i = 0; i < analyticsArray.length; i++) {
            const analytics = analyticsArray[i];

            switch (analytics.type) {
                case AnalyticsTypes.LinkDomains:
                    tracker('create', analytics.token, 'auto', {
                        allowLinker: true,
                    });
                    tracker('require', 'linker');
                    tracker('linker:autoLink', analytics.domains);
                    break;
                case AnalyticsTypes.TrackEvent:
                    getPageFromPathname().then(page => {
                        const { category } = page;
                        if (trackEventIsWhitelisted(analytics, category)) {
                            trackEventWithData(tracker, analytics, category);
                        }
                    });
                    break;
                case AnalyticsTypes.ChangePageView:
                    tracker('set', 'page', analytics.page);
                    tracker('send', 'pageview');
                    break;
                case AnalyticsTypes.SetDimension:
                    tracker(
                        'set',
                        determineSettingType(analytics.settingType) +
                            analytics.index,
                        analytics.value
                    );
                    break;
                default:
                    throw new Error('Please specify a valid analytics type');
            }
        }
    }

    return () => next => action => {
        ga =
            (typeof window !== 'undefined' && window.ga
                ? window.ga
                : undefined) || ga;
        const result = next(action);

        if (action.meta) {
            if (action.meta.analytics) {
                if (!ga || !domContentLoaded) {
                    _gaEventQueue.push(action);

                    if (!_gaCheckLoop) {
                        _gaCheckLoop = setInterval(checkingLoop, 100);
                    }
                } else {
                    processAction(action, ga);
                }
            }
        }

        return result;
    };
}

const GA = {
    AnalyticsTypes,
    SettingType,
    DimensionKeys,
    DimensionValues,
};

const analyticsLogger = createAnalyticsLogger();
analyticsLogger.withTracker = createAnalyticsLogger;

export { analyticsLogger, GA };
